/**
 * Pantalla de firma del consentimiento, pensada para pasarle la tableta a la paciente.
 *
 * Ocupa la pantalla entera a propósito: mientras ella lee y firma no puede ver la
 * lista de pacientes que hay detrás. Enseñarle quién más viene a la clínica sería
 * una cesión de datos que nadie ha autorizado.
 *
 * El texto lo sirve el servidor con su hash. Se devuelve ese mismo hash al firmar y
 * el servidor rechaza la firma si el texto ha cambiado entretanto: así queda probado
 * A QUÉ dijo que sí, que es justo lo que pide el artículo 7.1 del RGPD.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, Eraser, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { consentText, signConsent, revokeConsent, errMsg } from "@/api";

/** Recuadro para firmar con el dedo. Sin librerías: es un canvas y cuatro eventos. */
const SignaturePad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const dibujando = useRef(false);
  const [vacio, setVacio] = useState(true);

  // El canvas se dimensiona a su tamaño real en pantalla para que el trazo no salga
  // desplazado en la tableta, donde la densidad de píxeles no es 1.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1b2a4e";
  }, []);

  const punto = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const empezar = (e) => {
    e.preventDefault();
    dibujando.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = punto(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const mover = (e) => {
    if (!dibujando.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = punto(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (vacio) setVacio(false);
  };

  const terminar = () => {
    if (!dibujando.current) return;
    dibujando.current = false;
    onChange(vacio ? "" : canvasRef.current.toDataURL("image/png"));
  };

  const borrar = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setVacio(true);
    onChange("");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-brand-navy">Firma aquí</Label>
        <Button type="button" variant="ghost" size="sm" onClick={borrar} disabled={vacio}>
          <Eraser className="h-4 w-4 mr-1" /> Borrar
        </Button>
      </div>
      <canvas
        ref={canvasRef}
        data-testid="consent-signature-pad"
        className="w-full h-40 rounded-md border-2 border-dashed border-slate-300 bg-white touch-none"
        onPointerDown={empezar}
        onPointerMove={mover}
        onPointerUp={terminar}
        onPointerLeave={terminar}
      />
      {vacio && <p className="text-xs text-slate-500 mt-1">Con el dedo o con el lápiz, dentro del recuadro.</p>}
    </div>
  );
};

export const ConsentSignature = ({ patient, modo = "firmar", onClose, onDone }) => {
  const [texto, setTexto] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [nombre, setNombre] = useState(modo === "firmar" ? patient.name || "" : "");
  const [documento, setDocumento] = useState("");
  const [representante, setRepresentante] = useState("");
  const [firma, setFirma] = useState("");
  const [leido, setLeido] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    consentText()
      .then(setTexto)
      .catch((err) => toast.error(errMsg(err, "No se pudo cargar el consentimiento")))
      .finally(() => setCargando(false));
  }, []);

  const enviar = useCallback(
    async (acepta) => {
      if (!firma) return toast.error("Falta la firma");
      if (!nombre.trim() || !documento.trim()) return toast.error("Falta el nombre o el documento");
      setEnviando(true);
      try {
        const payload = {
          accepted: acepta,
          signer_name: nombre.trim(),
          signer_id_number: documento.trim(),
          signature: firma,
          text_sha256: texto.sha256,
          on_behalf: representante.trim(),
        };
        if (modo === "revocar") {
          await revokeConsent(patient.id, payload);
          toast.success("Consentimiento retirado");
        } else {
          await signConsent(patient.id, payload);
          toast.success(acepta ? "Consentimiento firmado" : "Queda registrado que no lo autoriza");
        }
        onDone?.();
        onClose();
      } catch (err) {
        toast.error(errMsg(err, "No se pudo registrar"));
      } finally {
        setEnviando(false);
      }
    },
    [firma, nombre, documento, representante, texto, modo, patient.id, onClose, onDone]
  );

  const revocando = modo === "revocar";

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="consent-screen">
      <header className="flex items-center justify-between px-5 py-3 border-b bg-brand-navy text-white">
        <div>
          <p className="text-xs uppercase tracking-wide opacity-80">
            {revocando ? "Retirar el consentimiento" : "Consentimiento informado"}
          </p>
          <h2 className="text-lg font-semibold">{patient.name}</h2>
        </div>
        <Button type="button" variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </header>

      {cargando ? (
        <div className="flex-1 grid place-items-center">
          <Loader2 className="h-6 w-6 animate-spin text-brand-navy" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-5 py-4 max-w-3xl w-full mx-auto">
          {!revocando && (
            <article
              data-testid="consent-text"
              className="prose prose-sm max-w-none text-slate-800 whitespace-pre-wrap leading-relaxed"
            >
              {texto?.text}
            </article>
          )}

          {revocando && (
            <p className="text-slate-800">
              Retiro el consentimiento que di para que mis sesiones se registren con NotaViva.
              A partir de ahora mi fisioterapeuta tomará las notas de la forma habitual. Esto no
              afecta a mi tratamiento, ni al precio, ni a la disponibilidad de cita.
            </p>
          )}

          <div className="mt-6 space-y-4 border-t pt-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="c-nombre" className="text-brand-navy">Nombre y apellidos</Label>
                <Input id="c-nombre" data-testid="consent-name" value={nombre} onChange={(e) => setNombre(e.target.value)} className="mt-1 h-11" />
              </div>
              <div>
                <Label htmlFor="c-doc" className="text-brand-navy">DNI o NIE</Label>
                <Input id="c-doc" data-testid="consent-id" value={documento} onChange={(e) => setDocumento(e.target.value)} className="mt-1 h-11" />
              </div>
            </div>

            <div>
              <Label htmlFor="c-rep" className="text-brand-navy">
                Si firma el padre, la madre o un representante legal, ¿qué relación tiene? (opcional)
              </Label>
              <Input id="c-rep" data-testid="consent-behalf" value={representante} onChange={(e) => setRepresentante(e.target.value)} className="mt-1 h-11" placeholder="madre, padre, tutora…" />
            </div>

            <SignaturePad onChange={setFirma} />

            {!revocando && (
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  data-testid="consent-read"
                  checked={leido}
                  onChange={(e) => setLeido(e.target.checked)}
                  className="mt-1"
                />
                <span>He leído esta información, he podido preguntar y me lo han aclarado.</span>
              </label>
            )}
          </div>
        </div>
      )}

      <footer className="border-t px-5 py-4 bg-slate-50">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row gap-3 sm:justify-end">
          {revocando ? (
            <Button type="button" data-testid="consent-revoke" onClick={() => enviar(false)} disabled={enviando || !firma} className="h-12 bg-brand-navy">
              {enviando ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Retirar el consentimiento
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" data-testid="consent-no" onClick={() => enviar(false)} disabled={enviando || !firma || !leido} className="h-12">
                No lo autorizo
              </Button>
              <Button type="button" data-testid="consent-yes" onClick={() => enviar(true)} disabled={enviando || !firma || !leido} className="h-12 bg-brand-navy">
                {enviando ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Sí, lo autorizo
              </Button>
            </>
          )}
        </div>
        {texto?.version && (
          <p className="max-w-3xl mx-auto text-xs text-slate-500 mt-2 text-right">
            Texto versión {texto.version}
          </p>
        )}
      </footer>
    </div>
  );
};
